import { useEffect, useState } from 'react'
import type { Mismatch, ResolutionType } from '../types'
import { fetchActiveMismatches, acknowledge, resolve } from '../lib/api'
import { MismatchTable } from '../components/MismatchTable'
import { ResolveModal } from '../components/ResolveModal'
import { useBrandFilter } from '../lib/brand'

export function ToReview() {
  const [brand] = useBrandFilter()
  const [rows, setRows] = useState<Mismatch[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resolving, setResolving] = useState<Mismatch | null>(null)

  async function load() {
    try {
      setError(null)
      setRows(await fetchActiveMismatches())
    } catch (e) {
      setError((e as Error).message)
    }
  }

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand])

  async function handleAck(m: Mismatch) {
    try {
      await acknowledge(m.id)
      await load()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function handleResolveSubmit(type: ResolutionType, notes: string) {
    if (!resolving) return
    await resolve(resolving.id, type, notes)
    setResolving(null)
    await load()
  }

  const byType = (rows ?? []).reduce<Record<string, number>>((acc, r) => {
    acc[r.mismatch_type] = (acc[r.mismatch_type] ?? 0) + 1
    return acc
  }, {})

  const TYPE_PILL: Record<string, string> = {
    missing_in_omega: 'border-red-500/30 bg-red-500/10 text-red-200',
    missing_in_psp: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
    status_mismatch: 'border-purple-500/30 bg-purple-500/10 text-purple-200',
    amount_mismatch: 'border-blue-500/30 bg-blue-500/10 text-blue-200',
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <h1 className="text-2xl font-semibold whitespace-nowrap">
          {rows ? rows.length : '…'} active
        </h1>
        <div className="flex flex-wrap gap-2">
          {Object.entries(byType).map(([t, n]) => (
            <span
              key={t}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-full text-xs ${TYPE_PILL[t] ?? 'border-slate-700 bg-slate-800/60 text-slate-200'}`}
            >
              <span className="font-mono font-semibold">{n}</span>
              <span>{t.replace(/_/g, ' ')}</span>
            </span>
          ))}
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-200 rounded px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {rows && (
        <MismatchTable
          rows={rows}
          onAck={handleAck}
          onResolve={(m) => setResolving(m)}
          showBrand={brand === 'ALL'}
        />
      )}

      {resolving && (
        <ResolveModal
          mismatch={resolving}
          onClose={() => setResolving(null)}
          onSubmit={handleResolveSubmit}
        />
      )}
    </div>
  )
}
